import React, { ReactElement, useEffect } from 'react';

import {
  Loader, Text, usePageNumberFromUrl, usePerPageValueFromUrl,
} from 'idsk-react-components';
import { useSearchParams } from 'react-router-dom';
import { MobilePerPageSelect, Pagination, useResponsiveContext } from 'slovlex-idsk-react-components';
import { useParams } from 'react-router';
import {
  defaultPerPageValue,
  perPageDataArray,
} from '../../../../constants/PaginationPerPageConstants';
import UlohyListTable from '../MojeUlohyList/component/UlohyListTable';
import useUlohyOrganizacieDetail from './hooks/useUlohyOrganizacieDetail';
import './ulohyOrganizacieDetail.scss';
import getPaginationTotalLabel from '../../../../function/getPaginationTotalLabel';
import FilterInput from '../../../FilterInput';
import routes from '../../../../routing/routes';

interface Props {
  setPageStatus?: (status: number) => void;
}

const UlohyOrganizacieDetail = ({ setPageStatus }: Props): ReactElement => {
  const { page } = usePageNumberFromUrl('page');
  const [searchParams, setSearchParams] = useSearchParams();
  const { perPageValue } = usePerPageValueFromUrl('perPage', perPageDataArray, defaultPerPageValue);
  const {
    uuid: pouzivatelUuid,
  } = useParams<{ uuid: string }>();
  const {
    ulohyOrganizacieDetailStatus,
    ulohyOrganizacieDetailPage,
    reloadUlohyPage,
    searchText,
    pageNumber,
  } = useUlohyOrganizacieDetail();
  const { isSmallScreen } = useResponsiveContext();

  useEffect(() => {
    setPageStatus(ulohyOrganizacieDetailStatus.responseCode);
  }, [ulohyOrganizacieDetailStatus.responseCode, setPageStatus]);

  if (!ulohyOrganizacieDetailPage) {
    return <Loader loading={ulohyOrganizacieDetailStatus.loading} />;
  }

  if (ulohyOrganizacieDetailPage.totalElements === 0 && !searchText) {
    return (
      <>
        <FilterInput
          filterParam={searchText}
          route={routes.ulohyOrganizacie.detail.withParams(pouzivatelUuid)}
          label="Zadajte číslo LP alebo názov"
        />
        <Loader loading={ulohyOrganizacieDetailStatus.loading}>
          <Text type="p" size="l" className="text-center">
            V súčasnosti nemá tento člen organizácie priradené úlohy.
          </Text>
        </Loader>
      </>
    );
  }

  if (ulohyOrganizacieDetailPage.totalElements === 0) {
    return (
      <>
        <FilterInput
          filterParam={searchText}
          route={routes.ulohyOrganizacie.detail.withParams(pouzivatelUuid)}
          label="Zadajte číslo LP alebo názov"
        />
        <Loader loading={ulohyOrganizacieDetailStatus.loading}>
          <Text type="p" size="l" className="text-center">
            Nenašli sa žiadne záznamy.
          </Text>
        </Loader>
      </>
    );
  }

  return (
    <div className="ulohy-organizacie-detail">
      <FilterInput
        filterParam={searchText}
        route={routes.ulohyOrganizacie.detail.withParams(pouzivatelUuid)}
        label="Zadajte číslo LP alebo názov"
      />
      {perPageDataArray.length > 0 && (
        <>
          {isSmallScreen ? (
            <div className="per-page-select">
              <MobilePerPageSelect
                onRecordsPerPageChange={(value) => {
                  const updatedSearchParams = new URLSearchParams(searchParams.toString());
                  updatedSearchParams.set('perPage', value.toString());
                  setSearchParams(updatedSearchParams.toString());
                }}
                recordsPerPage={perPageDataArray}
                perPageValue={perPageValue}
                totalText={getPaginationTotalLabel(ulohyOrganizacieDetailPage, page, 'Úlohy')}
              />
            </div>
          ) : (
            <Text className="pagination-total-label">
              {getPaginationTotalLabel(ulohyOrganizacieDetailPage, page, 'Úlohy')}
            </Text>
          )}
        </>
      )}
      <Loader loading={ulohyOrganizacieDetailStatus.loading}>
        <>
          {ulohyOrganizacieDetailPage && (
            <UlohyListTable
              reloadUlohyPage={reloadUlohyPage}
              ulohyPage={ulohyOrganizacieDetailPage}
            />
          )}
          <div className="ulohy-organizacie-list-pagination-row">
            <Pagination
              hideTotal
              number={pageNumber}
              size={ulohyOrganizacieDetailPage.size}
              totalElements={ulohyOrganizacieDetailPage.totalElements}
              onRecordsPerPageChange={(value) => {
                const updatedSearchParams = new URLSearchParams(searchParams.toString());
                updatedSearchParams.set('perPage', value.toString());
                setSearchParams(updatedSearchParams.toString());
              }}
              recordsPerPage={perPageDataArray}
              perPageValue={perPageValue}
            />
          </div>
        </>
      </Loader>
    </div>
  );
};

export default UlohyOrganizacieDetail;
